/**
 * `useCurrentUser` — live read of the signed-in user's own
 * `users/{uid}` Firestore document.
 *
 * The Profile editor's "Your profile" section binds to this so the
 * customer can edit `displayName`, `email`, `role` (their job title
 * in the studio), `altPhone`, `altEmail`, and `photoURL` from web.
 *
 * Firestore rules already permit a user to read + write their own
 * `users/{uid}` doc (see `firestore.rules` lines 246-268). No new
 * rule needed.
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';

export type CurrentUserDoc = {
  uid: string;
  data: Record<string, unknown>;
};

export type UseCurrentUserResult = {
  userDoc: CurrentUserDoc | null;
  loading: boolean;
  error: string | null;
};

export function useCurrentUser(): UseCurrentUserResult {
  const { user, loading: authLoading } = useAuth();
  const [userDoc, setUserDoc] = useState<CurrentUserDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setUserDoc(null);
      setLoading(false);
      return;
    }
    return onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        // An empty user doc is normal for new signups — the auth.ts
        // mintCustomToken callable creates the Firebase Auth user but
        // doesn't pre-seed the Firestore doc. The Profile form
        // handles this by treating every field as optional.
        setUserDoc({ uid: user.uid, data: snap.data() ?? {} });
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [user, authLoading]);

  return { userDoc, loading, error };
}
